import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from '../Auth/AuthContext';

export function useAdminCheck() {
  const { user } = useAuth(); // get current user
  const [isAdmin, setIsAdmin] = useState(null); // null = not yet known
  const [isDelivery, setIsDelivery] = useState(null); // null = not yet known
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) return; // wait for user

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const data = userDoc.data();
        // Check both old isAdmin field and new userType field
        const isAdminUser = data?.isAdmin === true || data?.userType === 'admin';
        const isDeliveryUser = data?.isDelivery === true || data?.userType === 'delivery';
        setIsAdmin(isAdminUser);
        setIsDelivery(isDeliveryUser);
        setUserId(user.uid)
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false); // fallback
      } finally {
        setLoading(false);
      }
    };

    checkAdmin();
  }, [user]);

  return { isAdmin, isDelivery, loading, userId };
}

// Enhanced hook for role-based access control
export function useUserRole() {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({
    isAdmin: false,
    isDelivery: false,
    isCustomer: false,
    canManageOrders: false,
    canViewAllOrders: false,
    canEditInventory: false
  });

  useEffect(() => {
    const checkUserRole = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const data = userDoc.data();
        
        // Determine user type (with backward compatibility)
        let userType = 'customer'; // default
        
        if (data?.userType) {
          userType = data.userType;
        } else if (data?.isAdmin === true) {
          userType = 'admin';
        } else if (data?.isDelivery === true) {
          userType = 'delivery';
        }
        
        setUserRole(userType);
        
        // Set permissions based on role
        const newPermissions = {
          isAdmin: userType === 'admin',
          isDelivery: userType === 'delivery',
          isCustomer: userType === 'customer',
          canManageOrders: userType === 'admin' || userType === 'delivery',
          canViewAllOrders: userType === 'admin' || userType === 'delivery',
          canEditInventory: userType === 'admin',
          canAssignOrders: userType === 'admin',
          canUpdateOrderStatus: userType === 'admin' || userType === 'delivery',
          canViewFinancials: userType === 'admin',
          canManageUsers: userType === 'admin'
        };
        
        setPermissions(newPermissions);
        
      } catch (err) {
        console.error('Error checking user role:', err);
        setUserRole('customer'); // fallback
        setPermissions({
          isAdmin: false,
          isDelivery: false,
          isCustomer: true,
          canManageOrders: false,
          canViewAllOrders: false,
          canEditInventory: false
        });
      } finally {
        setLoading(false);
      }
    };

    checkUserRole();
  }, [user]);

  return { 
    userRole, 
    permissions, 
    loading, 
    userId: user?.uid,
    // Convenience methods
    isAdmin: permissions.isAdmin,
    isDelivery: permissions.isDelivery,
    isCustomer: permissions.isCustomer
  };
}
